import { CONTACT_EMAIL, LegalPage, List, Section } from './LegalPage';

export function Terms() {
  return (
    <LegalPage
      title="Terms of Service"
      description="The agreement between you and ClientBridge: what the service does, what you may do with it, and where responsibility sits."
    >
      <Section heading="1. The agreement">
        <p>
          These terms are between you and ClientBridge. By creating an account or
          using the app, you accept them. If you are using ClientBridge on behalf
          of a company, you confirm you may bind that company.
        </p>
      </Section>

      <Section heading="2. What the service is">
        <p>
          ClientBridge is a log of client interactions. You record a name, a
          phone number, and a note; we store them and show them back to you as a
          timeline. That is the whole product. It is not a system of record for
          anything regulated, and it should not be your only copy of information
          you cannot afford to lose.
        </p>
      </Section>

      <Section heading="3. Your account">
        <p>
          You sign in with Apple or Google. Keep that account secure — anyone who
          controls it controls your ClientBridge data. You must be at least 16
          years old to use the service.
        </p>
      </Section>

      <Section heading="4. Your content stays yours">
        <p>
          Everything you log belongs to you. We claim no ownership of it. We
          store and process it only to provide the service to you — never to sell
          it, mine it for advertising, or train models on it.
        </p>
        <p>
          You grant us only the narrow permission needed to run the app: to
          store, back up, and display your content back to you.
        </p>
      </Section>

      <Section heading="5. Your responsibilities">
        <p>
          The people you log are real people, and you are responsible for how you
          record them. Specifically, you agree not to:
        </p>
        <List
          items={[
            <>
              record information about someone without a lawful basis to do so,
              or in breach of any privacy or anti-spam law that applies to you;
            </>,
            <>
              store special-category data — health, financial account numbers,
              government identifiers — in a note;
            </>,
            <>
              use the service to harass anyone, or to send unsolicited marketing;
            </>,
            <>
              attempt to access another user’s data, probe our infrastructure, or
              interfere with the service;
            </>,
            <>
              resell or white-label the service without our written agreement.
            </>,
          ]}
        />
      </Section>

      <Section heading="6. Availability">
        <p>
          We aim to keep ClientBridge running, but we do not promise it will be
          uninterrupted or error-free. We may change, suspend, or discontinue
          features. If we discontinue the service altogether, we will give you
          reasonable notice and a way to export your data.
        </p>
      </Section>

      <Section heading="7. No warranty">
        <p>
          The service is provided <strong>“as is”</strong>, without warranties of
          any kind, to the fullest extent the law allows. We do not warrant that
          it will meet your requirements or that stored data will always be
          available.
        </p>
      </Section>

      <Section heading="8. Limitation of liability">
        <p>
          To the fullest extent permitted by law, ClientBridge is not liable for
          indirect, incidental, or consequential damages, or for lost profits,
          lost business, or lost data. Where liability cannot be excluded, it is
          limited to the greater of the amount you paid us in the twelve months
          before the claim, or ₹1,000.
        </p>
        <p>
          Nothing in these terms excludes liability that cannot lawfully be
          excluded.
        </p>
      </Section>

      <Section heading="9. Ending it">
        <p>
          You can stop using ClientBridge and delete your account at any time —
          see the{' '}
          <a href="/privacy">Privacy Policy</a> for what deletion removes. We may
          suspend or close an account that breaches these terms, and will tell
          you why unless we are legally prevented from doing so.
        </p>
      </Section>

      <Section heading="10. Changes">
        <p>
          We may update these terms. If a change materially affects your rights,
          we will update the effective date above and notify you in the app
          before it takes effect. Continuing to use ClientBridge after that means
          you accept the new terms.
        </p>
      </Section>

      <Section heading="11. Governing law">
        <p>
          These terms are governed by the laws of India, and the courts of India
          have exclusive jurisdiction over any dispute arising from them.
        </p>
      </Section>

      <Section heading="12. Contact">
        <p>
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        </p>
      </Section>
    </LegalPage>
  );
}
